// These are the expected types for the API responses.  This is currently manually maintained, but in the future
// we could auto-generate these from the go source code with something like: https://github.com/gzuidhof/tygo.

// Utility types
type ObjectValues<T> = T[keyof T];

// From /go/pkg/datacollect/data/user.go
export type User = {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  // Roles and permissions are scoped to clinic_id: what the user holds at that clinic plus what they hold globally.
  // Holding either at one clinic says nothing about another, so all three are present only when the caller asked for
  // roles or permissions and named the clinic to report them for.
  clinic_id?: string;
  roles?: string[];
  permissions?: PermissionType[];
};

// From /go/pkg/datacollect/data/userlogin.go
export type UserLogin = {
  email: string;
  password: string;
};

export type UserLoginResponse = {
  token: string;
};

// From /go/pkg/datacollect/data/petinfo.go
export type PetOwnerInfo = {
  user_id: string;
  first_name: string;
  last_name: string;
};

export type PetInfo = {
  id: string;
  name: string;
  clinic_pet_id: string;
  owner: PetOwnerInfo[];
};

export type PetNameChange = {
  pet_id: string;
  new_name: string;
};

export type PetAndClinicInfo = {
  id: string;
  name: string;
  clinic_pet_id: string;
  // integration_url is optional but if exists, it will always have url and is_clickable defined.
  integration_url?: { url: string; is_clickable: boolean };
};

export type PetCheckupStats = {
  num_checkups: number;
  num_items_uploaded: number;
  num_surveys_answered: number;
};

// From /go/pkg/datacollect/data/requests.go
export type VideoType = 'default';
export type UploadItemType = 'video';
export type CheckupRequestStatus = 'not_started' | 'not_complete' | 'complete';
export type CheckupRequestAnalysisStatus = 'not_started' | 'processing' | 'gait_anomaly' | 'no_anomaly_detected';

export const CheckupTags: Record<string, string> = {
  in_clinic: 'in_clinic',
  in_home: 'in_home',
  at_event: 'at_event',
  companion_experiment: 'companion_experiment'
};
export type CheckupTag = ObjectValues<typeof CheckupTags>;

// LocationCheckupTags is a list of all the tags that relate to the location of the checkup.
export const LocationCheckupTags: CheckupTag[] = ['in_clinic', 'in_home', 'at_event'];

export type VideoUploadRequest = {
  video_type: VideoType;
};

export type CreateCheckupRequest = {
  clinic_id: string;
  clinic_pet_id?: string;
  pet_id?: string;
  requests?: VideoUploadRequest[];
  tags?: CheckupTag[];
  create_pet_if_not_found?: boolean;
  pet_name?: string;
  species?: string;
};

export type UploadRequestItem = {
  item_id: string;
  item_type: UploadItemType;
  item: {
    video_type: VideoType;
  };
  upload_count: number;
};

export type CreateCheckupResponse = {
  id: string;
  unknown_pet_id: boolean;
  pet_id: string;
  tags: CheckupTag[];
};

export type AnsweredDetails = {
  survey_id: string;
  answers_id: string;
};

export type CheckupRequestStatusResponse = {
  id: string;
  pet_id: string;
  clinic_pet_id: string;
  pet_name: string;
  vet_first_name: string;
  vet_last_name: string;
  status: CheckupRequestStatus;
  analysis_status: CheckupRequestAnalysisStatus;
  request_created: string;
  request_completed?: string;
  answered_surveys?: AnsweredDetails[];
  tags: CheckupTag[];
  uploaded_at?: string;
};

export type CheckupRequestInfo = {
  id: string;
  pet_id: string;
  pet_name: string;
  clinic_id: string;
  clinic_name: string;
  clinic_pet_id: string;
  vet_first_name: string;
  vet_last_name: string;
  requested_by_user_id: string;
  request_created: string;
  num_asset_ids: number;
  num_direct_uploads: number;
  num_items_requested: number;
  requests: UploadRequestItem[];
  answered_surveys?: AnsweredDetails[];
  tags?: CheckupTag[];
  status: CheckupRequestStatus;
  analysis_status: CheckupRequestAnalysisStatus;
  items?: UploadRequestItem[];
};

export type CheckupsForPetWithSurveyAnswer = {
  id: string;
  request_created: string;
  clinic_id: string;
  pet_id: string;
  pet_name: string;
  requested_user_id: string;
  survey_id: string;
};

export type RequestSingleItemUploadStatus = {
  item_id: string;
  direct_upload: string;
  asset_id: string;
};

export type ItemUploadSignedUrl = {
  url: string;
  valid_until: string;
  item_id: string;
  video_already_uploaded: boolean;
};

export type ItemPlaybackUrl = {
  url: string;
  valid_until: string;
  item_id: string;
};

// CreateCheckupWithCompletedCheckupIdRequest asks the server to create a new checkup that mirrors an existing one. Only
// the id of the existing (typically just completed) checkup is sent; the server copies the clinic, pet, requesting user,
// and location tags from that checkup. Kept in sync with the Go type of the same name.
export type CreateCheckupWithCompletedCheckupIdRequest = {
  completed_checkup_id: string;
};

export type CreateCheckupWithCompletedCheckupIdResponse = {
  checkup_id: string;
};

export type UpdateCheckupLocationRequest = {
  checkup_id: string;
  location_tag: CheckupTag;
};

// Auth Payload
// Permissions, kept in sync with the `permission` enum in go/pkg/datacollect/data/permissions.go and the database. The
// front end gates on these permissions rather than on role names.
export const Permissions = {
  admin_access: 'admin.access',
  clinic_member: 'clinic.member',
  checkup_review: 'checkup.review',
  item_review: 'item.review',
  checkup_label_print: 'checkup.label_print',
  checkup_advanced_info: 'checkup.advanced_info'
} as const;
export type PermissionType = (typeof Permissions)[keyof typeof Permissions];

export type JwtPayload = {
  user_id: string;
  clinic_id: string;
  permissions: PermissionType[];
  password_needs_reset: boolean;
};

export type ApiErrorResponse = {
  error: string;
};

export type BaseCheckupUrl = {
  base_url: string;
};

export type ChangePasswordRequest = {
  old_password: string;
  new_password: string;
};

// From /go/pkg/datacollect/data/surveys.go
export type SurveyInfo = {
  survey_id: string;
  version: number;
  name: string;
  title: string;
  description: string;
  created_at: string;
};

export type SurveyFull<T> = SurveyInfo & {
  questions: T[];
};

export type SurveyAnswersRequest = {
  survey_id: string;
  version: number;
  checkup_request_ids: string[];
  answers: Record<string, unknown>;
  answered_by_user_id: string;
};

export type SurveyAnswersUpdateRequest = {
  survey_id: string;
  version: number;
  answers_id: string;
  answers: Record<string, unknown>;
  answered_by_user_id: string;
  checkups_to_add?: string[];
};

export type SurveyAnswersInfo = {
  answers_id: string;
  survey_id: string;
  version: number;
  survey_name: string;
  survey_title: string;
  survey_description: string;
  clinic_id: string;
  answered_by: string;
  answered_at: string;
};

export type SurveyAnswersAndInfo = SurveyAnswersInfo & {
  answers: Record<string, unknown>;
  checkup_request_ids: string[];
};

// From /go/pkg/datacollect/data/itemreviews.go
export type ItemToReview = {
  item_id: string;
  item_type: UploadItemType;
  request_id: string;
  requested_at: string;
  clinic_id: string;
  clinic_name: string;
  pet_id: string;
  pet_name: string;
  first_name: string;
  last_name: string;
  num_reviews: number;
};

export type ItemReviewAnswerRequest = {
  item_id: string;
  survey_id: string;
  version: number;
  answers: Record<string, unknown>;
};

// From /go/pkg/datacollect/data/reviews.go

export type AnsweredCheckupsWithBonusVideos = {
  pet_id: string;
  latest_answered_request_id: string;
  requested_at: string;
  answers_id: string;
  clinic_pet_id: string;
  clinic_id: string;
  clinic_name: string;
  pet_name: string;
  num_unanswered_checkups: number;
};
// From /go/pkg/datacollect/data/clinics.go

export type ClinicInfo = {
  id: string;
  name: string;
};

// From /go/pkg/datacollect/data/errors.go
export type AdminClinicSwitchErrorResponse = {
  error: string;
  switch_clinic_id: string;
};

// From /go/pkg/chiu/envs.go
export type Environment = 'production' | 'staging' | 'development';

// From /go/pkg/datacollect/data/itemanalysis.go
export type AnalysisResultsForItem = {
  status: string;
  // The key for the results Record is the name of the analysis (e.g. "gait")
  results: Record<string, unknown>;
};

export type AnalysisResultsForCheckup = {
  // The key for the items Record is the item id
  items: Record<string, AnalysisResultsForItem>;
};

export type GaitMlResultClip = {
  end_sec: number;
  start_sec: number;
};

export type GaitMlResults = {
  analysis: 'gait';
  confidence: string;
  relevant_clips: GaitMlResultClip[];
  result_received: string;
  status: string;
  error_type: string;
};

// From /go/pkg/datacollect/data/mux.go
export type MuxSignedPlaybackUrlResponse = {
  token: string;
  valid_until: string;
  item_id: string;
  playback_id: string;
};

export type MuxAnimatedGifParams = {
  name: string;
  width: number;
  fps: number;
  start_sec: number;
  end_sec: number;
};

export type MuxAnimatedGifUrlsRequest = {
  item_id: string;
  clips: MuxAnimatedGifParams[];
};

export type MuxAnimatedGifUrlResponse = {
  name: string;
  url: string;
  valid_until: string;
  start_sec: number;
  end_sec: number;
};
